import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const navLinks = [
  { label: 'Home', href: 'https://itmunch.com/', external: true },
  { label: 'Benefits', href: '#benefits' },
  { label: 'Process', href: '#process' },
  { label: 'Why iTMunch', href: '#why-us' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-in-out ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <nav className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-700 ease-in-out ${
        scrolled ? 'h-16 md:h-16' : 'h-20 md:h-24'
      }`}>
        <a href="#hero" data-testid="navbar-logo" className="flex items-center group">
          <img
            src={scrolled
              ? "https://itmunch.com/wp-content/uploads/2024/01/itmunch-logo_black-02.png"
              : "https://itmunch.com/wp-content/uploads/2021/01/ITT-MUNCH_LOGO-4-1-1024x512_800x400.webp"
            }
            alt="iTMunch - Where Technology Meets You"
            className={`${scrolled ? 'h-[36px]' : 'h-[56px]'} w-auto object-contain transition-all duration-700 ease-in-out`}
          />
        </a>

        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-medium transition-all duration-700 ease-in-out ${
                  scrolled ? 'text-slate-600 hover:text-[#4AC5DB]' : 'text-white/80 hover:text-white'
                }`}
                {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <a href="#hero">
            <Button
              data-testid="nav-cta-button"
              className="rounded-full bg-[#4AC5DB] hover:bg-[#3AB0C5] text-white px-6 h-10 text-sm font-semibold shadow-lg shadow-[#4AC5DB]/20 transition-all hover:-translate-y-0.5"
            >
              Get Started <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-slate-700"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white border-t border-slate-100 shadow-lg"
        >
          <ul className="flex flex-col p-4 gap-1">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="block py-3 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                  {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li className="pt-2">
              <a href="#hero" onClick={() => setMobileOpen(false)}>
                <Button
                  data-testid="mobile-cta-button"
                  className="w-full rounded-full bg-[#4AC5DB] hover:bg-[#3AB0C5] text-white h-11 font-semibold"
                >
                  Get Started <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
